import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { GraphQLService } from '@app/services';
import { queryOperation, mutationLikeNews } from '@app/graphql-queries';
import { DataService, ColorService } from '@app/services';
import { LanguageGuard } from '@app/guards';
import { Operation } from '@app/models';

@Injectable({
  providedIn: 'root'
})
export class OperationService {
    
    constructor(
        private translateService: TranslateService,
        private graphQLService: GraphQLService,
        private languageGuard: LanguageGuard,
        private dataService: DataService
    ) { }
    
    async init(){
        this.initCSS();
        this.createManifest();
        await this.languageGuard.initLanguages();
    }
    
    initCSS(){
        if(this.dataService.operation.value){
            
            const primaryColor = this.dataService.operation.style?.theme?.mainColor;
            const secondaryColor = this.dataService.operation.style?.theme?.secondaryColor;
            if (primaryColor && secondaryColor){
                document.documentElement.style.setProperty('--demooz-color-primary', primaryColor);           
                document.documentElement.style.setProperty('--demooz-color-primary-contrast', secondaryColor);
            }
            
            const buttonBackgroundColor = this.dataService.operation.style?.common?.button?.backgroundColor;
            const buttonTextColor = this.dataService.operation.style?.common?.button?.color;            
            if (buttonBackgroundColor && buttonTextColor){
                document.documentElement.style.setProperty('--demooz-button-background-color', buttonBackgroundColor);           
                document.documentElement.style.setProperty('--demooz-button-text-color', buttonTextColor);
            }
            
            let settings = this.dataService.operation.value.settings;
            
            if(settings?.fonts?.normal?.URL){
                let fontNormal = new FontFace("custom-font", "url("+settings?.fonts?.normal?.URL?.replace('http://','https://')+")", {  style: "normal",  weight: "normal"});
                document.fonts.add(fontNormal);      
            }

            if(settings?.fonts?.bold?.URL){
                let fontBold = new FontFace("custom-font", "url("+settings?.fonts?.bold?.URL?.replace('http://','https://')+")", {  style: "normal",  weight: "bold"});
                document.fonts.add(fontBold);      
            }

            if(settings?.fonts?.italic?.URL){
                let fontItalic = new FontFace("custom-font", "url("+settings?.fonts?.italic?.URL?.replace('http://','https://')+")", {  style: "italic",  weight: "normal"});
                document.fonts.add(fontItalic);      
            }

            if(settings?.fonts?.boldItalic?.URL){
                let fontBoldItalic = new FontFace("custom-font", "url("+settings?.fonts?.boldItalic?.URL?.replace('http://','https://')+")", {  style: "italic",  weight: "bold"});
                document.fonts.add(fontBoldItalic);      
            }
            
            document.documentElement.style.setProperty('--ion-font-family', "'custom-font', system-ui, Arial");
                        
            this.resetCustomStyles();                
            if(this.dataService.operation.style?.customCSS && this.dataService.operation.style?.customCSS != ''){                
                let style = document.createElement('style'); 
                style.id = 'custom_styles'; 
                style.textContent = this.dataService.operation.style.customCSS;
                document.body.prepend(style); 
            }
            
        }
    }
    
    createManifest(){
        
        this.resetManifest();
        
        if(this.dataService.operation){
            let head = document.querySelector('head');
            let title = (this.dataService.operation.value.pwa?.name && this.dataService.operation.value.pwa?.name != '' ? this.dataService.operation.value.pwa?.name : this.dataService.operation.value.name);
            document.title = title;

            let manifest: any = { 
                name: title,
                icons: []
            };
            
            let logo = (this.dataService.operation.pwaLogo ? this.dataService.operation.pwaLogo : this.dataService.operation.darkLogo);
            if(logo){
                let regexPng = /\.png$/i;
                let regexJpeg = /\.(jpeg|jpg)$/i;
                let mimetype = (regexPng.test(logo) ? "image/png" : (regexJpeg.test(logo) ? "image/jpeg" : null));
                if(mimetype){
                    logo = logo.replace(/^http:\/\//,'https://');
                    manifest.icons.push({
                        src: logo, 
                        sizes: "512x512", 
                        type: mimetype
                    });

                    let favicon = document.createElement('link');
                    favicon.setAttribute('rel', 'icon');
                    favicon.setAttribute('href', logo);                     
                    if(head){
                        head.appendChild(favicon);    
                    }
                }
            } 

            let content = encodeURIComponent(JSON.stringify(manifest));
            let url = "data:application/manifest+json,"+content;

            let element = document.createElement('link');
            element.setAttribute('rel', 'manifest');
            element.setAttribute('href', url);   
            if(head){            
                head.appendChild(element);
            }
        }
        else{
            document.title = " Espace Ambassadeur";
        }
    }
    
    resetCSS(){
        this.resetManifest();
        this.resetCustomStyles();
    }
    
    resetManifest() {
        let existManifest = document.querySelector('head link[rel="manifest"]');
        if(existManifest){
            existManifest.remove();
        } 
        let existFavicon = document.querySelector('head link[rel="icon"]');
        if(existFavicon){
            existFavicon.remove();
        } 
    }
    
    resetCustomStyles() {
        let existCustomStyles = document.body.querySelector('style#custom_styles');
        if(existCustomStyles){
            existCustomStyles.remove();
        }
    }
    
    async fetchOperation(operationID: string): Promise<any> {
        let data: any = null;
        let response = await this.graphQLService.query({
            query: queryOperation,
            variables: {
              ID: operationID
            },
            fetchPolicy: 'no-cache' 
        },(this.dataService.ambassador.value !== null ? 'ambassador' : 'public'));      
        if(response?.data?.Operation){
            data = response.data.Operation;            
        }
        return data;
    }
    
    async refreshOperation(){
        let operation = await this.fetchOperation(this.dataService.operation.value.ID);
        if(operation){
            this.dataService.operation = new Operation(operation);  
        }
    }
    
    async postLikeNews(operationID: string, newsID: string, like: boolean){
        let status: boolean = false;
        let response = await this.graphQLService.mutate({   
            mutation: mutationLikeNews,
            variables: {                
                ID: operationID,
                params: JSON.stringify({               
                    newsID: newsID,
                    like: like
                }),
            }       
        });     
        if(response?.data?.NewsPostLike){
            status = true;
        }
        return status;
    }
}
